import React from 'react';
import docxIcon from './docx.svg';
import pdfIcon from './pdf.svg';
import rtfIcon from './rtf.svg';
import txtIcon from './txt.svg';
import jsonIcon from './json.svg';
import mdIcon from './md.svg';

export type FileType = 'docx' | 'pdf' | 'rtf' | 'txt' | 'json' | 'md';

interface FileTypeIconProps {
  fileType?: FileType;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const iconMap: Record<FileType, string> = {
  docx: docxIcon,
  pdf: pdfIcon,
  rtf: rtfIcon,
  txt: txtIcon,
  json: jsonIcon,
  md: mdIcon,
};

const sizeMap = {
  sm: 'h-4 w-4', // 16px
  md: 'h-5 w-5', // 20px
  lg: 'h-6 w-6', // 24px - Google Docs size
};

export function FileTypeIcon({ fileType, size = 'md', className = '' }: FileTypeIconProps) {
  // Always default to RTF if fileType is undefined or not found
  const safeFileType: FileType = (fileType && iconMap[fileType]) ? fileType : 'rtf';
  const iconPath = iconMap[safeFileType] || iconMap['rtf']; // Double fallback to RTF
  const sizeClass = sizeMap[size];

  // Debug: Log when fallback is used
  if (!fileType || !iconMap[fileType]) {
    console.log('FileTypeIcon: Using RTF fallback for fileType:', fileType, 'iconPath:', iconPath);
  }

  return (
    <img
      src={iconPath}
      alt={`${safeFileType.toUpperCase()} file`}
      className={`${sizeClass} ${className} object-contain`}
      style={{
        filter: 'none', // Ensure icons maintain their original colors
      }}
    />
  );
}

// Helper function to determine file type from filename or extension
export function getFileTypeFromExtension(filename: string): FileType {
  const extension = filename.toLowerCase().split('.').pop();
  
  switch (extension) {
    case 'docx':
      return 'docx';
    case 'pdf':
      return 'pdf';
    case 'rtf':
      return 'rtf';
    case 'txt':
      return 'txt';
    case 'json':
      return 'json';
    case 'md':
    case 'markdown':
      return 'md';
    default:
      return 'rtf'; // Default fallback to RTF
  }
}

// Helper function to get file type from MIME type
export function getFileTypeFromMimeType(mimeType: string): FileType {
  switch (mimeType) {
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      return 'docx';
    case 'application/pdf':
      return 'pdf';
    case 'application/rtf':
    case 'text/rtf':
      return 'rtf';
    case 'text/plain':
      return 'txt';
    case 'application/json':
      return 'json';
    case 'text/markdown':
      return 'md';
    default:
      return 'rtf'; // Default fallback to RTF
  }
}
